import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Share, Users, X } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { AppCard } from '../../components/ui/AppCard';
import { AvatarView } from '../../components/ui/AvatarView';
import { Button } from '../../components/ui/Button';
import { IconButton } from '../../components/ui/IconButton';
import { EmptyState } from '../../components/ui/EmptyState';
import { Toast } from '../../components/ui/Toast';
import { Toggle } from '../../components/ui/Toggle';
import { UpgradePrompt } from '../../components/UpgradePrompt';
import { PublishDisclosureSheet } from '../../components/channels/PublishDisclosureSheet';
import { useFeatureStrict } from '../../lib/features';
import { PERMISSION_PRESETS, presetForPermissions, type ChannelMemberPermissions } from '../../types';
import {
  searchUsers,
  fetchUserProfileById,
  fetchChannelMembers,
  addChannelMember,
  removeChannelMember,
  approveChannelMember,
  setChannelMemberPermissions,
  addChannelOwner,
  removeChannelOwner,
  fetchShareLinkStatus,
  enableShareLink,
  disableShareLink,
  setShareLinkPassword,
  setShareLinkRequestToJoin,
  setChannelSearchable,
  inviteChannelByEmail,
  LimitError,
  type UserProfile,
  type ChannelMember,
  type ShareLinkStatus,
} from '../../api/dataApi';
import { queryClient, queryKeys } from '../../lib/queryClient';

interface SearchResult {
  userId: string;
  profile: UserProfile;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ChannelShareScreen() {
  const { channelId } = useParams();
  const navigate = useNavigate();
  const { channel, applyChannelStats, isChannelOwner, profile, updateChannelPublicChartFiltersAllowed, ensureChannelLoaded } = useStore();
  const c = channel(channelId ?? '');

  // Deep link / hard reload: a channel the caller owns but hasn't
  // joined/followed into `channels` isn't in the store yet - without this,
  // `c` stays undefined until some other screen happens to load it.
  // ensureChannelLoaded fetches it into `viewedChannels` once (see its own
  // comment in StoreContext.tsx) and is a no-op if it's already loaded.
  //
  // This screen used to keep its own separate `fetched` local-state copy
  // (a plain fetchChannel() call, bypassing the store entirely) instead of
  // this - every toggle handler below calls applyChannelStats(c.id, ...)
  // to reflect a change immediately, but applyChannelStats only ever
  // writes into `channels`/`viewedChannels`; it had no way to reach that
  // local `fetched` copy. The result: a toggle's request genuinely
  // succeeded server-side, but this screen kept rendering the pre-toggle
  // value until a hard reload re-ran the fetch from scratch and picked up
  // the new state. Routing through ensureChannelLoaded/viewedChannels
  // instead (the same pattern ChannelDetailsScreen.tsx already uses) means
  // `c` is sourced from the one place applyChannelStats actually updates,
  // so a toggle reflects immediately like the rest of this screen's
  // switches always appeared to (members, request-to-join, etc. all
  // already went through the store correctly).
  useEffect(() => {
    ensureChannelLoaded(channelId ?? '');
  }, [channelId, ensureChannelLoaded]);

  // Owner-only screen - every control here (share link enable/disable,
  // password, searchable, request-to-join, add/remove/approve member,
  // email invite) is an owner-or-manageChannel action the backend rejects
  // with 403 for anyone else. A non-owner who deep-links here (or reached
  // it before the Share entry points were owner-gated) gets bounced to the
  // channel instead of a screenful of buttons that only produce errors.
  // This used to make an exception for *public* channels (letting a non-
  // owner view/copy the already-public link), but that also exposed the
  // member-management UI, whose "Add" button surfaced a raw engine error
  // (channel/member_add.wsl's FetchCallerMemberDoc JSONGet miss) rather
  // than a clean "not allowed" - not worth it for a link the viewer can
  // already get from the public channel view. Mirrors ChannelManageScreen
  // .tsx's identical owner-only guard and the Share button gating in
  // ChatScreen.tsx.
  useEffect(() => {
    // Wait for the profile too - isChannelOwner is always false while
    // profile.userId is still "" (fresh load), which would bounce the real
    // owner off this screen.
    if (c && profile.userId && !isChannelOwner(c)) {
      navigate(`/channels/${c.id}`, { replace: true });
    }
  }, [c, profile.userId, isChannelOwner, navigate]);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [members, setMembers] = useState<ChannelMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);
  const [savingPermId, setSavingPermId] = useState<string | null>(null);
  const [error, setError] = useState('');
  // A member-permission edit the backend refused with {code:
  // "advanced_permissions"} (roadmap 4b-f - hand-tuned toggles below
  // Business, see api/dataApi.ts's setChannelMemberPermissions). Presets
  // never trigger this; only the granular per-toggle editor can.
  const [limitMessage, setLimitMessage] = useState('');

  const [shareLink, setShareLink] = useState<ShareLinkStatus | null>(null);
  const [loadingShareLink, setLoadingShareLink] = useState(true);
  const [togglingShareLink, setTogglingShareLink] = useState(false);
  const [togglingSearchable, setTogglingSearchable] = useState(false);
  // Studio v2: publishing (searchable on) goes through a disclosure sheet.
  const studioV2 = useFeatureStrict('studio.v2');
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [togglingRequestToJoin, setTogglingRequestToJoin] = useState(false);
  // Password field state is separate from the four toggles above - it has
  // its own "Save" action (savingPassword) rather than firing on every
  // keystroke, since it POSTs to the server (see setShareLinkPassword in
  // dataApi.ts).
  const [shareLinkUsePassword, setShareLinkUsePassword] = useState(false);
  const [shareLinkPasswordInput, setShareLinkPasswordInput] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [publicUrlCopied, setPublicUrlCopied] = useState(false);
  const [invitingVia, setInvitingVia] = useState<'email' | 'share' | null>(null);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (!c) return;
    let cancelled = false;
    setLoadingMembers(true);
    // Routes through queryClient.query (see lib/queryClient.ts) -
    // dedupes this against a React StrictMode double-invoke or a fast
    // remount re-running this effect for the same channel, and retries a
    // transient failure with backoff instead of surfacing it immediately.
    queryClient
      .query({ queryKey: queryKeys.channelMembers(c.nickname), queryFn: () => fetchChannelMembers(c.nickname) })
      .then((list) => {
        if (!cancelled) {
          setMembers(list);
          setLoadingMembers(false);
        }
      });
    setLoadingShareLink(true);
    fetchShareLinkStatus(c.nickname).then((status) => {
      if (!cancelled) {
        setShareLink(status);
        // Reflects current server state so the checkbox isn't wrong on
        // load - the password itself never comes back from the server
        // (only whether one is set), so the field starts empty regardless.
        setShareLinkUsePassword(status?.passwordProtected ?? false);
        setLoadingShareLink(false);
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c?.nickname]);

  // Debounced search - fires 300ms after typing stops, and only once at
  // least 2 characters are entered, rather than a request per keystroke.
  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const timeout = setTimeout(async () => {
      // Routes through queryClient.query - the 300ms debounce above
      // already limits how often this fires, but a slow/flaky connection
      // can still leave an earlier search in flight when a later one for
      // the SAME term would otherwise fire again (e.g. the effect re-running
      // for an unrelated reason before the timeout's own term changes);
      // dedup and retry-with-backoff cover that.
      const hits = await queryClient.query({ queryKey: queryKeys.userSearch(term), queryFn: () => searchUsers(term) });
      const profiles = await Promise.all(hits?.map((h) => fetchUserProfileById(h.userId)));
      setResults(
        hits
          ?.map((hit, i) => (profiles[i] ? { userId: hit.userId, profile: profiles[i] as UserProfile } : null))
          .filter((r): r is SearchResult => r !== null)
      );
      setSearching(false);
    }, 300);
    return () => clearTimeout(timeout);
  }, [query]);

  if (!c) {
    return (
      <div className="flex flex-col gap-4">
        <ScreenHeader title="Share channel" />
        <EmptyState icon={Users} title="Channel missing" subtitle="The channel is no longer available." />
      </div>
    );
  }

  // Pending join requests (see backend/workflows/channel/
  // request_access.wsl) live in the same channel:members index as real
  // members, distinguished only by status - split them apart for display.
  const acceptedMembers = members.filter((m) => m.status === 'accepted');
  const pendingRequests = members.filter((m) => m.status === 'pending');
  // People an owner invited who haven't accepted yet (see backend/
  // workflows/channel/member_add.wsl). The owner can revoke, but only the
  // invitee can accept - so no Approve button here, unlike join requests.
  const invitedMembers = members.filter((m) => m.status === 'invited');

  const addMember = async (result: SearchResult) => {
    setAddingId(result.userId);
    setError('');
    try {
      const member = await addChannelMember(c.nickname, {
        userId: result.userId,
        email: result.profile.personal.email,
        nickname: result.profile.nickname,
        name: result.profile.name.fullname || result.profile.name.name,
        avatarSymbol: 'person.crop.circle',
      });
      setMembers((prev) => [...prev, member]);
      // See StoreContext.tsx's updateChannel for the identical rationale -
      // keeps the query cache truthful for the next query call
      // against this key, even though `members` above (not the cache) is
      // what actually renders.
      queryClient.invalidateQueries({ queryKey: queryKeys.channelMembers(c.nickname) });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to add member.');
    } finally {
      setAddingId(null);
    }
  };

  // Also used to deny/cancel a pending join request - member_remove.wsl's
  // owner/manageChannel/self auth already covers both "owner denies" and
  // "requester cancels their own request" (see dataApi.ts's
  // removeChannelMember comment).
  const removeMember = async (userId: string) => {
    setRemovingId(userId);
    setError('');
    try {
      await removeChannelMember(c.nickname, userId);
      setMembers((prev) => prev.filter((m) => m.userId !== userId));
      queryClient.invalidateQueries({ queryKey: queryKeys.channelMembers(c.nickname) });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to remove member.');
    } finally {
      setRemovingId(null);
    }
  };

  // One member's permission override (roadmap 4a-f). Sends just the
  // changed key(s); the backend merges over the member's stored set (see
  // channel/member_permissions.wsl). textOnly/signalsOnly are opposing -
  // turning one on clears the other locally too, matching the server.
  const updateMemberPermission = async (
    userId: string,
    patch: Partial<ChannelMemberPermissions>
  ) => {
    setSavingPermId(userId);
    setError('');
    try {
      const updated = await setChannelMemberPermissions(c.nickname, userId, patch);
      setMembers((prev) => prev.map((m) => (m.userId === userId ? updated : m)));
      queryClient.invalidateQueries({ queryKey: queryKeys.channelMembers(c.nickname) });
    } catch (err) {
      if (err instanceof LimitError) {
        setLimitMessage(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Unable to update permissions.');
      }
    } finally {
      setSavingPermId(null);
    }
  };

  // Role presets (roadmap 4b-f): sends the full preset's permission set in
  // one request - always allowed regardless of tier (see
  // presetForPermissions/backend's matching check), unlike hand-tuning the
  // toggles below one at a time.
  const applyPreset = (userId: string, permissions: ChannelMemberPermissions) =>
    updateMemberPermission(userId, permissions);

  // Co-ownership (channel.ownerIds). Promoting/demoting a member is its own
  // endpoint (POST/DELETE /channels/{nickname}/owners) - see
  // backend/workflows/channel/owner_add.wsl. The backend also keeps the
  // member doc's permissions.manageChannel in lockstep, so the "Co-owner"
  // toggle below still reflects owner status either way.
  const toggleOwner = async (userId: string, makeOwner: boolean) => {
    if (!c) return;
    setSavingPermId(userId);
    setError('');
    try {
      const ownerIds = makeOwner
        ? await addChannelOwner(c.nickname, userId)
        : await removeChannelOwner(c.nickname, userId);
      applyChannelStats(c.id, { ownerIds });
      setMembers((prev) =>
        prev.map((m) =>
          m.userId === userId ? { ...m, permissions: { ...m.permissions, manageChannel: makeOwner } } : m
        )
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.channelMembers(c.nickname) });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update channel owners.');
    } finally {
      setSavingPermId(null);
    }
  };

  const approveRequest = async (userId: string) => {
    setApprovingId(userId);
    setError('');
    try {
      const updated = await queryClient.query({
        queryKey: queryKeys.channelMemberApprove(c.nickname, userId),
        queryFn: () => approveChannelMember(c.nickname, userId),
        retry: 0,
        staleTime: 0,
      });
      setMembers((prev) => prev.map((m) => (m.userId === userId ? { ...m, ...updated } : m)));
      queryClient.invalidateQueries({ queryKey: queryKeys.channelMembers(c.nickname) });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to approve this request.');
    } finally {
      setApprovingId(null);
    }
  };

  // Both invite paths need the share link enabled first (auto-enabling it
  // if it isn't already on) - the recipient doesn't need an account to
  // open it, matching the read-only share-link flow above.
  const ensureShareLinkUrl = async (): Promise<string> => {
    if (shareLink?.enabled && shareLinkUrl) return shareLinkUrl;
    // Write, not a read - retry: 0. staleTime: 0 too: this is a distinct
    // enable call each time it runs (guarded above by the enabled+token
    // check), so it must never serve a stale token from an earlier call.
    // See queryKeys.shareLinkEnable's own comment on why the key
    // deliberately excludes the password (no longer even sent here at all
    // - see enableShareLink's own comment).
    const status = await queryClient.query({
      queryKey: queryKeys.shareLinkEnable(c.nickname),
      queryFn: () => enableShareLink(c.nickname),
      retry: 0,
      staleTime: 0,
    });
    setShareLink(status);
    return `${window.location.origin}/shared/${c.nickname}/${status.token}`;
  };

  // Sent by the backend itself over real SMTP now (see
  // backend/workflows/channel/invite_email.wsl) - not a mailto: handoff to
  // the visitor's own mail client. query is already validated as an email
  // by the caller (only rendered when EMAIL_PATTERN.test(query.trim())).
  const inviteViaEmail = async () => {
    setInvitingVia('email');
    setError('');
    try {
      await ensureShareLinkUrl();
      // Write, not a read - retry: 0, staleTime: 0 (a deliberate resend to
      // the same address later must always reach the network, not serve a
      // cached "already sent" result - staleTime: 0 still dedupes a
      // genuinely concurrent double-click, just not a later, separate one).
      await queryClient.query({
        queryKey: queryKeys.inviteEmail(c.nickname, query.trim()),
        queryFn: () => inviteChannelByEmail(c.nickname, query.trim()),
        retry: 0,
        staleTime: 0,
      });
      setNotice(`Invite sent to ${query.trim()}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to send the invite.');
    } finally {
      setInvitingVia(null);
    }
  };

  // Web Share API opens the OS share sheet (Messages/WhatsApp/etc are
  // whatever the device itself offers there) - not supported on most
  // desktop browsers, so this falls back to copying the message to the
  // clipboard for the visitor to paste in themselves.
  const inviteViaShare = async () => {
    setInvitingVia('share');
    setError('');
    try {
      const link = await ensureShareLinkUrl();
      if (navigator.share) {
        await navigator.share({ title: `Join ${c.title} on Zmist`, text: `You're invited to view ${c.title} on Zmist`, url: link });
      } else {
        await navigator.clipboard.writeText(`You're invited to view ${c.title} on Zmist: ${link}`);
        setNotice('Link copied - paste it into a text, WhatsApp, or any messaging app.');
      }
    } catch (err) {
      // AbortError just means the visitor closed the share sheet without
      // picking anything - not a real failure, don't surface it as an error.
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Unable to share the invite.');
    } finally {
      setInvitingVia(null);
    }
  };

  // Switch 1: share link on/off. Independent of the other three - see
  // this screen's render section and each dataApi.ts function's comment
  // for how (previously this also forced isPublic/password together with
  // it; no longer does).
  const toggleShareLink = async (next: boolean) => {
    setTogglingShareLink(true);
    setError('');
    try {
      if (next) {
        // See ensureShareLinkUrl's identical comment on retry/staleTime.
        const status = await queryClient.query({
          queryKey: queryKeys.shareLinkEnable(c.nickname),
          queryFn: () => enableShareLink(c.nickname),
          retry: 0,
          staleTime: 0,
        });
        setShareLink(status);
      } else {
        await queryClient.query({
          queryKey: queryKeys.shareLinkDisable(c.nickname),
          queryFn: () => disableShareLink(c.nickname),
          retry: 0,
          staleTime: 0,
        });
        setShareLink((prev) => (prev ? { ...prev, enabled: false } : prev));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update the share link.');
    } finally {
      setTogglingShareLink(false);
    }
  };

  // Switch 2: searchable as public on/off - independent of the share link
  // (see backend/workflows/channel/searchable_enable.wsl's header
  // comment for the history of why these used to be coupled).
  const toggleSearchable = async (next: boolean) => {
    setTogglingSearchable(true);
    setError('');
    try {
      const result = await queryClient.query({
        queryKey: queryKeys.channelSearchable(c.nickname, next),
        queryFn: () => setChannelSearchable(c.nickname, next),
        retry: 0,
        staleTime: 0,
      });
      applyChannelStats(c.id, { isPublic: result.isPublic });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update searchability.');
    } finally {
      setTogglingSearchable(false);
    }
  };

  // Switch 3: require a password - has its own Save action (below) rather
  // than firing per-keystroke or per-toggle, since unchecking it clears
  // the password server-side immediately (no separate "clear" affordance
  // needed) while checking it just reveals the field until Save is
  // pressed.
  const savePassword = async () => {
    if (shareLinkUsePassword && !shareLinkPasswordInput) {
      setError('Enter a password, or uncheck "Require a password".');
      return;
    }
    setSavingPassword(true);
    setError('');
    try {
      const result = await queryClient.query({
        queryKey: queryKeys.shareLinkPassword(c.nickname),
        queryFn: () => setShareLinkPassword(c.nickname, shareLinkUsePassword ? shareLinkPasswordInput : ''),
        retry: 0,
        staleTime: 0,
      });
      setShareLink((prev) => (prev ? { ...prev, passwordProtected: result.passwordProtected } : prev));
      setShareLinkPasswordInput('');
      setNotice(result.passwordProtected ? 'Password saved.' : 'Password removed.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update the share link password.');
    } finally {
      setSavingPassword(false);
    }
  };

  // Switch 4: request on join - lets a visitor with the link submit a
  // join request instead of only getting the read-only view (see
  // backend/workflows/channel/request_access.wsl).
  const toggleRequestToJoin = async (next: boolean) => {
    setTogglingRequestToJoin(true);
    setError('');
    try {
      const result = await queryClient.query({
        queryKey: queryKeys.shareLinkRequestToJoin(c.nickname, next),
        queryFn: () => setShareLinkRequestToJoin(c.nickname, next),
        retry: 0,
        staleTime: 0,
      });
      setShareLink((prev) => (prev ? { ...prev, requestToJoin: result.requestToJoin } : prev));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update request-to-join.');
    } finally {
      setTogglingRequestToJoin(false);
    }
  };

  const shareLinkUrl =
    shareLink?.enabled && shareLink.token ? `${window.location.origin}/shared/${c.nickname}/${shareLink.token}` : '';

  return (
    <div className="flex flex-col gap-5">
      <ScreenHeader title="Share channel" />

      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-black/70 dark:text-white/70">Add by email or @nickname</span>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by email or @nickname"
          className="rounded-xl border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-3.5 py-2.5 text-[15px] outline-none focus:border-blue-500"
        />
      </div>

      {query.trim().length >= 2 && (
        <div className="flex flex-col gap-2">
          {searching && <span className="text-sm text-black/50 dark:text-white/50">Searching...</span>}
          {!searching && results.length === 0 && EMAIL_PATTERN.test(query.trim()) && (
            <AppCard className="flex flex-col gap-3">
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="font-semibold truncate">No account with this email yet</span>
                <span className="text-sm text-black/50 dark:text-white/50 truncate">{query.trim()}</span>
              </div>
              <div className="flex gap-2 items-center">
                <Button onClick={inviteViaEmail} disabled={invitingVia !== null} className="flex-1">
                  {invitingVia === 'email' ? 'Preparing...' : 'Send Email'}
                </Button>
                <IconButton
                  icon={Share}
                  onClick={inviteViaShare}
                  disabled={invitingVia !== null}
                  aria-label="Share via SMS, WhatsApp, or another app"
                />
              </div>
            </AppCard>
          )}
          {!searching && results.length === 0 && !EMAIL_PATTERN.test(query.trim()) && (
            <span className="text-sm text-black/50 dark:text-white/50">No matches.</span>
          )}
          {results.map((result) => {
            // An owner "add" now sends an invite the person must accept
            // (see backend member_add.wsl) - show "Invited" until they do.
            // A "request to join" visitor shows as "Pending" (awaiting the
            // owner's approval, below).
            const existing = members.find((m) => m.userId === result.userId);
            const alreadyInvited = existing?.status === 'invited';
            const alreadyPending = existing?.status === 'pending';
            const alreadyMember = existing !== undefined && !alreadyInvited && !alreadyPending;
            const alreadyListed = alreadyInvited || alreadyPending || alreadyMember;
            return (
              <AppCard key={result.userId} className="flex items-center gap-3">
                <AvatarView symbol="person.crop.circle" colorHex="#3B82F6" size={44} />
                <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                  <span className="font-semibold truncate">{result.profile.name.fullname || result.profile.name.name}</span>
                  <span className="text-sm text-black/50 dark:text-white/50 truncate">
                    {result.profile.nickname} · {result.profile.personal.email}
                  </span>
                </div>
                <Button
                  variant={alreadyListed ? 'bordered' : 'primary'}
                  onClick={() => addMember(result)}
                  disabled={alreadyListed || addingId === result.userId}
                >
                  {alreadyInvited
                    ? 'Invited'
                    : alreadyPending
                      ? 'Pending'
                      : alreadyMember
                        ? 'Added'
                        : addingId === result.userId
                          ? 'Inviting...'
                          : 'Invite'}
                </Button>
              </AppCard>
            );
          })}
        </div>
      )}

      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-black/70 dark:text-white/70">Members</span>
        {loadingMembers && <span className="text-sm text-black/50 dark:text-white/50">Loading...</span>}
        {!loadingMembers && acceptedMembers.length === 0 && (
          <EmptyState icon={Users} title="No members yet" subtitle="Search above to add someone." />
        )}
        {acceptedMembers.map((member) => {
          const expanded = expandedMemberId === member.userId;
          const p = member.permissions;
          const savingPerm = savingPermId === member.userId;
          return (
            <AppCard key={member.userId} className="flex flex-col gap-2.5">
              <div className="flex items-center gap-3">
                <AvatarView symbol={member.avatarSymbol || 'person.crop.circle'} colorHex="#3B82F6" size={44} />
                <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                  <span className="font-semibold truncate">{member.name || member.nickname}</span>
                  <span className="text-sm text-black/50 dark:text-white/50 truncate">{member.nickname}</span>
                </div>
                <span className="shrink-0 rounded-full text-[11px] font-medium px-2 py-0.5 bg-emerald-600/15 text-emerald-600 dark:text-emerald-400">
                  Accepted
                </span>
                <IconButton
                  icon={X}
                  onClick={() => removeMember(member.userId)}
                  disabled={removingId === member.userId}
                  aria-label="Remove member"
                />
              </div>
              <button
                type="button"
                onClick={() => setExpandedMemberId(expanded ? null : member.userId)}
                className="self-start text-sm text-blue-600 dark:text-blue-400"
              >
                {expanded ? 'Hide permissions' : 'Permissions'}
              </button>
              {expanded && (
                <div className="flex flex-col gap-2 border-t border-black/10 dark:border-white/10 pt-2.5">
                  <div className="flex flex-col gap-1">
                    <span className="text-[11px] font-medium text-black/50 dark:text-white/50">Role preset</span>
                    <div className="flex flex-wrap gap-1.5">
                      {PERMISSION_PRESETS.map((preset) => {
                        const active = presetForPermissions(p) === preset.id;
                        return (
                          <button
                            key={preset.id}
                            type="button"
                            disabled={savingPerm}
                            onClick={() => applyPreset(member.userId, preset.permissions)}
                            className={`rounded-full px-2.5 py-1 text-xs font-medium border disabled:opacity-50 ${
                              active
                                ? 'bg-blue-600 border-blue-600 text-white'
                                : 'border-black/15 dark:border-white/20 text-black/60 dark:text-white/60 hover:border-blue-400'
                            }`}
                          >
                            {preset.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <Toggle
                    label="Send messages"
                    checked={p.postMessages}
                    disabled={savingPerm}
                    onChange={(v) => updateMemberPermission(member.userId, { postMessages: v })}
                  />
                  <Toggle
                    label="Send attachments"
                    checked={p.uploadAttachments}
                    disabled={savingPerm}
                    onChange={(v) => updateMemberPermission(member.userId, { uploadAttachments: v })}
                  />
                  <Toggle
                    label="Import messages from CSV"
                    checked={p.importCsv}
                    disabled={savingPerm}
                    onChange={(v) => updateMemberPermission(member.userId, { importCsv: v })}
                  />
                  <Toggle
                    label="Send reports"
                    checked={p.sendReports}
                    disabled={savingPerm}
                    onChange={(v) => updateMemberPermission(member.userId, { sendReports: v })}
                  />
                  <Toggle
                    label="Text messages only"
                    checked={p.textOnly}
                    disabled={savingPerm}
                    onChange={(v) =>
                      updateMemberPermission(member.userId, { textOnly: v, ...(v ? { signalsOnly: false } : {}) })
                    }
                  />
                  <Toggle
                    label="Signals only"
                    checked={p.signalsOnly}
                    disabled={savingPerm}
                    onChange={(v) =>
                      updateMemberPermission(member.userId, { signalsOnly: v, ...(v ? { textOnly: false } : {}) })
                    }
                  />
                  <Toggle
                    label="Co-owner (full access)"
                    checked={(c.ownerIds ?? []).includes(member.userId) || p.manageChannel}
                    disabled={savingPerm || ((c.ownerIds ?? []).length <= 1 && (c.ownerIds ?? []).includes(member.userId))}
                    onChange={(v) => toggleOwner(member.userId, v)}
                  />
                </div>
              )}
            </AppCard>
          );
        })}
      </div>

      {/* Owner-only: a pending request's message is meant for the owner,
          and only the owner/manageChannel can approve or deny it anyway
          (see backend/workflows/channel/member_approve.wsl and
          member_remove.wsl's auth chains) - a non-owner reaching this
          screen (only possible when the channel is public, see this
          file's top-of-component guard) has no use for seeing it. */}
      {isChannelOwner(c) && pendingRequests.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-black/70 dark:text-white/70">Join requests</span>
          {pendingRequests.map((request) => (
            <AppCard key={request.userId} className="flex flex-col gap-2.5">
              <div className="flex items-center gap-3">
                <AvatarView symbol={request.avatarSymbol || 'person.crop.circle'} colorHex="#3B82F6" size={44} />
                <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                  <span className="font-semibold truncate">{request.name || request.nickname}</span>
                  <span className="text-sm text-black/50 dark:text-white/50 truncate">{request.nickname}</span>
                </div>
              </div>
              {request.message && <span className="text-sm text-black/70 dark:text-white/70">{request.message}</span>}
              <div className="flex gap-2">
                <Button onClick={() => approveRequest(request.userId)} disabled={approvingId === request.userId} className="flex-1">
                  {approvingId === request.userId ? 'Approving...' : 'Approve'}
                </Button>
                <Button
                  variant="bordered"
                  onClick={() => removeMember(request.userId)}
                  disabled={removingId === request.userId}
                  className="flex-1"
                >
                  {removingId === request.userId ? 'Denying...' : 'Deny'}
                </Button>
              </div>
            </AppCard>
          ))}
        </div>
      )}

      {/* Outgoing invites awaiting the invitee's accept. The owner can
          revoke (reuses removeMember), but can't accept on their behalf -
          see backend/workflows/channel/invite_accept.wsl. */}
      {isChannelOwner(c) && invitedMembers.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-black/70 dark:text-white/70">Invited</span>
          {invitedMembers.map((invited) => (
            <AppCard key={invited.userId} className="flex items-center gap-3">
              <AvatarView symbol={invited.avatarSymbol || 'person.crop.circle'} colorHex="#3B82F6" size={44} />
              <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                <span className="font-semibold truncate">{invited.name || invited.nickname}</span>
                <span className="text-sm text-black/50 dark:text-white/50 truncate">{invited.nickname} · awaiting response</span>
              </div>
              <Button
                variant="bordered"
                onClick={() => removeMember(invited.userId)}
                disabled={removingId === invited.userId}
              >
                {removingId === invited.userId ? 'Revoking...' : 'Revoke'}
              </Button>
            </AppCard>
          ))}
        </div>
      )}

      {/* "Searchable as public" (isPublic) and the old token-based Share
          Link are mutually exclusive ways to give out a read-only link,
          not two independent features to configure at once: once a
          channel is isPublic, it's already reachable, no-login, at
          /public/{nickname} (backend/workflows/channel/public_view.wsl,
          gated purely on isPublic) - a password-protected or
          approval-gated /shared/{nickname}/{token} link on TOP of that
          protects nothing, since the same content is already open at the
          plain nickname URL. So: isPublic on -> show only the /public/
          URL; isPublic off -> show the full Share Link flow (token,
          optional password, request-on-join) as the one way to hand out a
          read-only link to a channel that isn't otherwise public. Request
          on Join only exists for the token flow (request_access.wsl needs
          the token in its own URL - there's no equivalent for the
          tokenless /public/ page), which is why it disappears along with
          the rest of that section rather than being offered with nothing
          to attach it to. "Let visitors adjust chart filters" is the one
          control that genuinely applies to both URLs (both public pages
          read Channel.publicChartFiltersAllowed), so it stays outside this
          either/or. */}
      <div className="flex flex-col gap-3">
        {!loadingShareLink && (
          <>
            <Toggle label="Searchable as public" checked={c.isPublic} onChange={(v) => (v && studioV2 ? setConfirmPublish(true) : toggleSearchable(v))} disabled={togglingSearchable} loading={togglingSearchable} />
            {studioV2 && (
              <PublishDisclosureSheet
                channel={c}
                open={confirmPublish}
                busy={togglingSearchable}
                onCancel={() => setConfirmPublish(false)}
                onConfirm={async () => {
                  await toggleSearchable(true);
                  setConfirmPublish(false);
                }}
              />
            )}
            <span className="text-xs text-black/50 dark:text-white/50 -mt-2">Shows up in public channel search.</span>

            {c.isPublic ? (
              <AppCard className="flex items-center gap-2 -mt-1">
                <span className="text-sm truncate flex-1">{`${window.location.origin}/public/${c.nickname}`}</span>
                <Button
                  variant="bordered"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/public/${c.nickname}`);
                    setPublicUrlCopied(true);
                    setTimeout(() => setPublicUrlCopied(false), 2000);
                  }}
                >
                  {publicUrlCopied ? 'Copied!' : 'Copy'}
                </Button>
              </AppCard>
            ) : (
              <>
                <Toggle label="Share link" checked={shareLink?.enabled ?? false} onChange={(v) => toggleShareLink(v)} disabled={togglingShareLink} loading={togglingShareLink} />
                <span className="text-xs text-black/50 dark:text-white/50 -mt-2">
                  Anyone with the link below can view this channel read-only.
                </span>

                <Toggle
                  label="Request on join"
                  checked={shareLink?.requestToJoin ?? false}
                  onChange={(v) => toggleRequestToJoin(v)}
                  disabled={togglingRequestToJoin}
                  loading={togglingRequestToJoin}
                />
                <span className="text-xs text-black/50 dark:text-white/50 -mt-2">
                  Visitors with the link can request to join instead of only viewing read-only - you approve or deny each request above.
                </span>

                {/* Shown regardless of the share link's on/off state, so the
                    password can be set or changed at any time. Unlike the
                    switch above, saving this never rotates the share token
                    (see setShareLinkPassword's comment) - it's a genuinely
                    independent control. */}
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={shareLinkUsePassword}
                      onChange={(e) => setShareLinkUsePassword(e.target.checked)}
                    />
                    Require a password
                  </label>
                  {shareLinkUsePassword && (
                    <input
                      type="password"
                      value={shareLinkPasswordInput}
                      onChange={(e) => setShareLinkPasswordInput(e.target.value)}
                      placeholder={shareLink?.passwordProtected ? 'Enter a new password to change it' : 'Password'}
                      className="rounded-xl border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-3.5 py-2.5 text-[15px] outline-none focus:border-blue-500"
                    />
                  )}
                  <Button variant="bordered" onClick={savePassword} disabled={savingPassword}>
                    {savingPassword ? 'Saving...' : 'Save password'}
                  </Button>
                </div>

                {shareLink?.enabled && (
                  <AppCard className="flex items-center gap-2">
                    <span className="text-sm truncate flex-1">{shareLinkUrl}</span>
                    <Button
                      variant="bordered"
                      onClick={() => {
                        navigator.clipboard.writeText(shareLinkUrl);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </Button>
                  </AppCard>
                )}
                {shareLink?.enabled && shareLink.passwordProtected && (
                  <span className="text-xs text-black/50 dark:text-white/50">
                    Anyone with this link needs the password to view this channel read-only.
                  </span>
                )}
                {shareLink?.enabled && !shareLink.passwordProtected && (
                  <span className="text-xs text-black/50 dark:text-white/50">
                    Anyone with this link can view this channel read-only, no account needed.
                  </span>
                )}
              </>
            )}

            {/* Off by default - a public visitor's Charts tab (or, under
                the "report" public page design, its always-shown hero
                chart - see PublicReportHero.tsx) is locked to whatever
                you last configured on the Charts tab's own "Filters &
                settings" panel (Channel.chartSettings, synced there via
                SignalActivityChart's onSettingsChange), with the controls
                that would let a visitor change it hidden. Flipping this
                on hands visitors those same interactive controls - each
                visitor's own changes then persist only in their own
                browser's localStorage (SignalActivityChart's usual
                per-channel usePersistedState, keyed by this channel's nickname -
                never written back to the channel doc), the same way
                ChartScreen's own Filters & settings panel already works
                for you. */}
            <Toggle
              label="Let visitors adjust chart filters"
              checked={c.publicChartFiltersAllowed}
              onChange={(v) => updateChannelPublicChartFiltersAllowed(v, c.id)}
            />
            <span className="text-xs text-black/50 dark:text-white/50 -mt-2">
              Lets a visitor change the interval, metric, and signal/sender filters on the public chart themselves, instead of only seeing it exactly as you configured it.
            </span>
          </>
        )}
      </div>

      {error && <Toast message={error} onDismiss={() => setError('')} />}
      {notice && <Toast message={notice} type="success" onDismiss={() => setNotice('')} />}
      <UpgradePrompt
        open={!!limitMessage}
        onClose={() => setLimitMessage('')}
        message={limitMessage}
        title="Advanced permissions"
      />
    </div>
  );
}
